"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "@clerk/nextjs";
import { UserDetails } from "@/types/credits";

interface CreditContextType {
  userDetails: UserDetails | null;
  loading: boolean;
  error: string | null;
  fetchUserDetails: () => Promise<void>;
  updateUserDetails: (newDetails: Partial<UserDetails>) => void;
  refreshCredits: () => Promise<void>;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

export function CreditProvider({ children }: { children: React.ReactNode }) {
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { userId } = useAuth();

  // Use a ref to track the last refresh time
  const lastRefreshTimeRef = useRef(0);
  // Use a ref to track pending refresh promise
  const pendingRefreshRef = useRef<Promise<void> | null>(null);

  const fetchUserDetails = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/user-info");

      if (!response.ok) {
        throw new Error("Failed to fetch user details");
      }

      const Responsedata = await response.json();
      const { data } = Responsedata;


      setUserDetails({
        creditBalance: data.creditBalance,
        currentPlan: data.currentPlan,
        storageUsed: data.storageUsed,
        totalCredits: data.totalCredits,
        renewalDate: data.renewalDate,
        subscriptionStatus: data.subscriptionStatus,
        canClaimCredits: data.canClaimCredits,
      });
    } catch (error) {
      setError("Error fetching user details");
      console.error("❌ Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateUserDetails = useCallback((newDetails: Partial<UserDetails>) => {
    setUserDetails((prevDetails) => {
      if (!prevDetails) return null;
      return { ...prevDetails, ...newDetails };
    });
  }, []);

  // Improved refreshCredits implementation
  const refreshCredits = useCallback(async (): Promise<void> => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

    // If we have a pending refresh and it's recent, reuse it
    if (pendingRefreshRef.current && timeSinceLastRefresh < 2000) {
      console.log("🔄 Using existing pending refresh (called within 2s)");
      return pendingRefreshRef.current;
    }

    // Create a new refresh promise
    console.log("🔄 Starting new refresh credits operation");
    const refreshPromise = fetchUserDetails().then(() => {
      // Clear the pending refresh reference after completion
      pendingRefreshRef.current = null;
    });

    // Store the promise and update the timestamp
    pendingRefreshRef.current = refreshPromise;
    lastRefreshTimeRef.current = now;

    return refreshPromise;
  }, [fetchUserDetails]);

  // Check URL for showBanner parameter to refresh credits when welcome banner is shown
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const showBanner = urlParams.get('showBanner');

      if (showBanner === 'true' && userId) {
        console.log("🎉 Welcome banner detected, refreshing credits");
        refreshCredits();
      }
    }
  }, [userId, refreshCredits]);

  useEffect(() => {
    if (userId) {
      console.log("🔄 Initial user details fetch");
      fetchUserDetails();
    }
  }, [userId, fetchUserDetails]);

  const contextValue = useMemo(
    () => ({
      userDetails,
      loading,
      error,
      fetchUserDetails,
      updateUserDetails,
      refreshCredits,
    }),
    [
      userDetails,
      loading,
      error,
      fetchUserDetails,
      updateUserDetails,
      refreshCredits,
    ]
  );

  return (
    <CreditContext.Provider value={contextValue}>
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error("useCredits must be used within a CreditProvider");
  }
  return context;
}
